import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Timeline,
  Typography,
} from 'antd'
import {
  AppstoreOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ForkOutlined,
  SafetyOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'

import * as dashboardTemplatesApi from '@/api/dashboardTemplates'
import type { DashboardTemplateOrgLifecycleItem } from '@/api/dashboardTemplates'
import * as usersApi from '@/api/users'
import type { LpuHealthItem } from '@/api/accessHealth'
import { useAccessHealth } from '@/hooks/useAccessHealth'
import { useOrganizations } from '@/hooks/useUsers'

const { Title, Paragraph, Text } = Typography

const LIFECYCLE_STATE_LABELS: Record<string, string> = {
  not_provisioned: 'Не создана копия для ЛПУ',
  inactive_template: 'Родительский шаблон отключен',
  disabled: 'Копия отключена',
  unmanaged: 'Выведено из managed-контура',
  in_sync: 'Синхронизировано',
  needs_initial_rollout: 'Нужна первая раскатка',
  behind_source: 'Отстает от родителя',
  ahead_of_source: 'Опережает родителя',
  locally_modified: 'Есть локальные изменения',
  unknown: 'Статус неизвестен',
}

const LIFECYCLE_STATE_COLORS: Record<string, string> = {
  not_provisioned: 'default',
  inactive_template: 'default',
  disabled: 'default',
  unmanaged: 'cyan',
  in_sync: 'green',
  needs_initial_rollout: 'blue',
  behind_source: 'orange',
  ahead_of_source: 'volcano',
  locally_modified: 'magenta',
  unknown: 'default',
}

const ACTION_LABELS: Record<string, string> = {
  provision: 'Создать копию ЛПУ',
  activate_template: 'Активировать родителя',
  restore_instance: 'Вернуть копию ЛПУ',
  review_instance: 'Ручная проверка',
  apply_rollout: 'Раскатать изменения',
  none: 'Действий не нужно',
}

const HEALTH_STATUS_LABELS: Record<string, string> = {
  ok: 'OK',
  warning: 'Внимание',
  error: 'Ошибка',
  info: 'Информация',
}

const HEALTH_STATUS_COLORS: Record<string, string> = {
  ok: 'green',
  warning: 'orange',
  error: 'red',
  info: 'blue',
}

function countBy<T>(items: T[], predicate: (item: T) => boolean): number {
  return items.reduce((count, item) => count + (predicate(item) ? 1 : 0), 0)
}

function resolveHealthStatus(item: LpuHealthItem | undefined): {
  color: string
  label: string
  note: string
} {
  if (!item) {
    return {
      color: 'default',
      label: 'Нет данных',
      note: 'Health snapshot пока не содержит выбранное ЛПУ.',
    }
  }

  return {
    color: HEALTH_STATUS_COLORS[item.status] || 'default',
    label: HEALTH_STATUS_LABELS[item.status] || item.status,
    note: item.note || 'RLS-слой не сообщает о проблемах.',
  }
}

export default function BiWorkspacePage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(
    () => searchParams.get('org') ?? undefined,
  )

  const { data: organizations = [], isLoading: orgsLoading } = useOrganizations()
  const { data: healthData, isLoading: healthLoading, refetch: refetchHealth } = useAccessHealth()

  const selectedOrg = useMemo(
    () => organizations.find((org) => org.id === selectedOrgId),
    [organizations, selectedOrgId],
  )
  const isPlatformOrg = selectedOrg?.kind === 'platform' || selectedOrg?.code === '000000'

  useEffect(() => {
    if (!selectedOrgId && organizations.length > 0) {
      setSelectedOrgId(organizations[0].id)
    }
  }, [organizations, selectedOrgId])

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (selectedOrgId) {
      next.set('org', selectedOrgId)
    } else {
      next.delete('org')
    }
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, selectedOrgId, setSearchParams])

  const { data: lifecycleItems = [], isLoading: lifecycleLoading } = useQuery({
    queryKey: ['bi-workspace-org-lifecycle', selectedOrgId],
    queryFn: () =>
      dashboardTemplatesApi.fetchDashboardTemplateOrgLifecycle({
        org_id: selectedOrgId!,
        include_inactive: true,
      }),
    enabled: !!selectedOrgId && !isPlatformOrg,
  })

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['bi-workspace-users', selectedOrgId],
    queryFn: () =>
      usersApi.fetchUsers({
        org_id: selectedOrgId,
        page: 1,
        page_size: 200,
      }),
    enabled: !!selectedOrgId,
  })

  const orgOptions = useMemo(
    () => organizations.map((org) => ({ value: org.id, label: `${org.code} — ${org.name}` })),
    [organizations],
  )

  const lpuHealth = useMemo(
    () => healthData?.lpu_health.find((item) => item.code === selectedOrg?.code),
    [healthData?.lpu_health, selectedOrg?.code],
  )
  const healthStatus = resolveHealthStatus(lpuHealth)

  const lifecycleSummary = useMemo(() => {
    const needsProvision = countBy(
      lifecycleItems,
      (item) => item.lifecycle_state === 'not_provisioned',
    )
    const rolloutReady = countBy(lifecycleItems, (item) => item.apply_eligible)
    const reviewRequired = countBy(lifecycleItems, (item) => item.review_required)
    const inactive = countBy(
      lifecycleItems,
      (item) => item.lifecycle_state === 'inactive_template' || item.lifecycle_state === 'disabled',
    )
    const inSync = countBy(lifecycleItems, (item) => item.lifecycle_state === 'in_sync')
    return {
      total: lifecycleItems.length,
      needsProvision,
      rolloutReady,
      reviewRequired,
      inactive,
      inSync,
      needsAction: needsProvision + rolloutReady + reviewRequired + inactive,
    }
  }, [lifecycleItems])

  const users = usersData?.items ?? []
  const yandexNeedsLink = countBy(
    users,
    (user) => user.login_yandex_expected && !user.login_yandex_linked,
  )
  const localUsers = countBy(users, (user) => user.login_expected_login_mode === 'local')
  const inactiveUsers = countBy(users, (user) => !user.is_active)

  const openTemplateRegistry = (templateId?: string) => {
    const params = new URLSearchParams()
    if (selectedOrgId) {
      params.set('org_lifecycle_org', selectedOrgId)
    }
    if (templateId) {
      params.set('template', templateId)
    }
    navigate(`/admin/dashboard-template-registry?${params.toString()}`)
  }

  const openUserWorkspace = () => {
    const suffix = selectedOrgId ? `?org=${selectedOrgId}` : ''
    navigate(`/admin/access-workspace${suffix}`)
  }

  const openDashboardAssignment = () => {
    const suffix = selectedOrgId ? `?org=${selectedOrgId}` : ''
    navigate(`/admin/dashboard-access-workspace${suffix}`)
  }

  const lifecycleColumns: ColumnsType<DashboardTemplateOrgLifecycleItem> = [
    {
      title: 'BI-объект',
      key: 'object',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          <Text type="secondary">{record.source_entry_title || record.source_workbook_title || record.family_key}</Text>
        </Space>
      ),
    },
    {
      title: 'Состояние в ЛПУ',
      key: 'state',
      width: 240,
      render: (_: unknown, record) => (
        <Space direction="vertical" size={4}>
          <Tag color={LIFECYCLE_STATE_COLORS[record.lifecycle_state] || 'default'}>
            {LIFECYCLE_STATE_LABELS[record.lifecycle_state] || record.lifecycle_state}
          </Tag>
          {record.instance_id ? (
            <Text type="secondary">{record.target_entry_title || record.target_entry_id}</Text>
          ) : (
            <Text type="secondary">Копии для ЛПУ нет</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Следующее действие',
      key: 'action',
      width: 240,
      render: (_: unknown, record) => (
        <Space direction="vertical" size={4}>
          <Text>{ACTION_LABELS[record.recommended_action] || record.recommended_action}</Text>
          {record.version_delta === null ? null : (
            <Text type="secondary">Delta версии: {record.version_delta}</Text>
          )}
        </Space>
      ),
    },
    {
      title: '',
      key: 'open',
      width: 150,
      render: (_: unknown, record) => (
        <Button size="small" onClick={() => openTemplateRegistry(record.template_id)}>
          Открыть
        </Button>
      ),
    },
  ]

  const isLoading = orgsLoading || healthLoading || lifecycleLoading || usersLoading

  return (
    <div>
      <Breadcrumb
        items={[
          { title: 'Администрирование' },
          { title: 'BI-центр' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Card
        style={{
          marginBottom: 16,
          border: 0,
          background: 'linear-gradient(135deg, #eefcfb 0%, #f6f8ff 58%, #fff7e8 100%)',
        }}
      >
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} lg={15}>
            <Space direction="vertical" size={8}>
              <Space size={8} wrap>
                <Title level={3} style={{ margin: 0 }}>
                  BI-центр управления
                </Title>
                <Tag color="cyan">экспериментальный контур</Tag>
              </Space>
              <Paragraph type="secondary" style={{ margin: 0, maxWidth: 860 }}>
                Одна точка входа для ежедневной настройки BI по ЛПУ: сотрудники, группы,
                RLS, видимость дашбордов, родительские шаблоны, копии ЛПУ и диагностика.
                Глубокие CRUD-экраны остаются доступными, но больше не требуют помнить,
                где расположен каждый параметр.
              </Paragraph>
            </Space>
          </Col>
          <Col xs={24} lg={9}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Text strong>Рабочий контур</Text>
              <Select
                showSearch
                placeholder="Выберите ЛПУ"
                value={selectedOrgId}
                options={orgOptions}
                optionFilterProp="label"
                loading={orgsLoading}
                onChange={setSelectedOrgId}
                style={{ width: '100%' }}
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {!selectedOrg ? (
        <Card>
          <Empty description="Выберите ЛПУ, чтобы собрать рабочую сводку" />
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {isPlatformOrg && (
            <Alert
              type="info"
              showIcon
              message="Платформенный контур Медцифры"
              description="Это не ЛПУ-витрина: обычный RLS preview и матрица ЛПУ не применяются. Доступ к данным должен управляться через platform roles и временное elevation."
            />
          )}
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12} xl={6}>
              <Card>
                <Statistic
                  title="Сотрудники ЛПУ"
                  value={usersData?.total ?? 0}
                  prefix={<TeamOutlined />}
                />
                <Space size={[8, 8]} wrap style={{ marginTop: 12 }}>
                  <Tag color={yandexNeedsLink > 0 ? 'orange' : 'green'}>
                    Яндекс не привязан {yandexNeedsLink}
                  </Tag>
                  <Tag>локальный вход {localUsers}</Tag>
                  <Tag color={inactiveUsers > 0 ? 'default' : 'green'}>закрыты {inactiveUsers}</Tag>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Card>
                <Statistic
                  title="Проверка RLS"
                  value={healthStatus.label}
                  prefix={<SafetyOutlined />}
                  valueStyle={{ color: healthStatus.color === 'red' ? '#cf1322' : undefined }}
                />
                <Text type="secondary">{healthStatus.note}</Text>
              </Card>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Card>
                <Statistic
                  title="Жизненный цикл BI"
                  value={lifecycleSummary.needsAction}
                  suffix={`/ ${lifecycleSummary.total}`}
                  prefix={<AppstoreOutlined />}
                />
                <Space size={[8, 8]} wrap style={{ marginTop: 12 }}>
                  <Tag color="blue">к раскатке {lifecycleSummary.rolloutReady}</Tag>
                  <Tag color="volcano">на ревью {lifecycleSummary.reviewRequired}</Tag>
                  <Tag>создать копии {lifecycleSummary.needsProvision}</Tag>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Card>
                <Statistic
                  title="В синхроне"
                  value={lifecycleSummary.inSync}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
                <Text type="secondary">BI-объекты без рекомендуемых действий</Text>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} align="stretch">
            <Col xs={24} xl={9}>
              <Card
                title="Что делать дальше"
                extra={
                  <Button size="small" onClick={() => refetchHealth()}>
                    Обновить проверку
                  </Button>
                }
                style={{ height: '100%' }}
              >
                <Spin spinning={isLoading}>
                  <Timeline
                    items={[
                      {
                        color: healthStatus.color === 'red' ? 'red' : 'green',
                        dot: healthStatus.color === 'red' ? <ExclamationCircleOutlined /> : undefined,
                        children: (
                          <Space direction="vertical" size={4}>
                            <Text strong>Проверить данные и RLS</Text>
                            <Text type="secondary">
                              Пользователи в PG: {lpuHealth?.pg_users ?? '—'}, строки в CH: {lpuHealth?.ch_rows ?? '—'}.
                            </Text>
                            <Button
                              size="small"
                              disabled={isPlatformOrg}
                              onClick={() => selectedOrg?.code && navigate(`/admin/lpu/${selectedOrg.code}/access-matrix`)}
                            >
                              Открыть матрицу ЛПУ
                            </Button>
                          </Space>
                        ),
                      },
                      {
                        color: yandexNeedsLink > 0 ? 'orange' : 'green',
                        children: (
                          <Space direction="vertical" size={4}>
                            <Text strong>Проверить пользователей и вход</Text>
                            <Text type="secondary">
                              Требуют Яндекс-привязки: {yandexNeedsLink}; локальный вход: {localUsers}.
                            </Text>
                            <Button size="small" onClick={openUserWorkspace}>
                              Открыть рабочее место
                            </Button>
                          </Space>
                        ),
                      },
                      {
                        color: lifecycleSummary.needsAction > 0 ? 'orange' : 'green',
                        children: (
                          <Space direction="vertical" size={4}>
                            <Text strong>Разобрать жизненный цикл BI</Text>
                            <Text type="secondary">
                              Создать копии: {lifecycleSummary.needsProvision}; к раскатке: {lifecycleSummary.rolloutReady};
                              на ревью: {lifecycleSummary.reviewRequired}.
                            </Text>
                            <Space wrap>
                              <Button size="small" onClick={() => openTemplateRegistry()}>
                                Родительские шаблоны
                              </Button>
                              <Button size="small" onClick={openDashboardAssignment}>
                                Назначение дашбордов
                              </Button>
                            </Space>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </Spin>
              </Card>
            </Col>

            <Col xs={24} xl={15}>
              <Card
                title="BI-объекты выбранного ЛПУ"
                extra={
                  <Space wrap>
                    <Button size="small" icon={<ForkOutlined />} onClick={() => openTemplateRegistry()}>
                      Реестр копий
                    </Button>
                    <Button size="small" icon={<AuditOutlined />} onClick={() => navigate('/admin/dashboard-publications')}>
                      Публикации
                    </Button>
                  </Space>
                }
              >
                {lifecycleItems.length === 0 && !lifecycleLoading ? (
                  <Alert
                    type="info"
                    showIcon
                    message="Для выбранного ЛПУ пока нет строк жизненного цикла BI"
                    description="Это может быть нормой для пустого dev-контура. Глубокий реестр шаблонов остается источником создания родительских объектов."
                  />
                ) : (
                  <Table
                    rowKey={(record) => `${record.template_id}-${record.instance_id || 'missing'}`}
                    size="small"
                    loading={lifecycleLoading}
                    columns={lifecycleColumns}
                    dataSource={lifecycleItems}
                    pagination={{ pageSize: 6, showSizeChanger: false }}
                  />
                )}
              </Card>
            </Col>
          </Row>

          <Card title="Куда ушла сложность">
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12} xl={6}>
                <Card size="small" hoverable onClick={openUserWorkspace}>
                  <Space direction="vertical" size={4}>
                    <Tag color="blue">люди</Tag>
                    <Text strong>Сотрудник, вход, группы, RLS</Text>
                    <Text type="secondary">Открывает рабочее место сотрудника с выбранным ЛПУ.</Text>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Card size="small" hoverable onClick={openDashboardAssignment}>
                  <Space direction="vertical" size={4}>
                    <Tag color="purple">видимость BI</Tag>
                    <Text strong>Группы и наборы дашбордов</Text>
                    <Text type="secondary">Массовое добавление или замена без ручного обхода таблиц.</Text>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Card size="small" hoverable onClick={() => openTemplateRegistry()}>
                  <Space direction="vertical" size={4}>
                    <Tag color="cyan">цикл BI</Tag>
                    <Text strong>Родительский объект и копии ЛПУ</Text>
                    <Text type="secondary">Копии, расхождения, раскатка, ревью и синхронизация.</Text>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Card size="small" hoverable onClick={() => navigate('/admin/access-health')}>
                  <Space direction="vertical" size={4}>
                    <Tag color="orange">контроль</Tag>
                    <Text strong>Проверка и аудит рисков</Text>
                    <Text type="secondary">RLS, прямые выдачи, ограничения ЛПУ и матрицы.</Text>
                  </Space>
                </Card>
              </Col>
            </Row>
          </Card>
        </Space>
      )}
    </div>
  )
}
